import { createContext, use, useRef, type SVGProps } from 'react';
import { Animate, AnimateTransform, SmilTimeline, usePlayOnceInView, type Key, type Keys } from '../smil';
import { HANDS, type ArtPath } from './coins-tile-art';
import { FINGERS, STACK, drawClosed } from './coins-tile-poses';

/**
 * `surface` is the card's own colour: each hand is filled with it so the coin it holds sits behind it. `coin`
 * fills the disc and `euro` draws the sign on it.
 */
const PALETTES = {
	sand: { surface: '#FFF7ED', ink: '#242F2C', coin: '#FF7D1D', euro: '#242F2C' },
	sage: { surface: '#EAEECF', ink: '#25302C', coin: '#25302C', euro: '#EAEECF' },
} as const;

type Palette = (typeof PALETTES)[keyof typeof PALETTES];

const Colours = createContext<Palette>(PALETTES.sand);
/** Below the artboard's bottom edge, so every hand starts out of the tile. */
const FROM_BELOW = 260;
/** How much deeper in the closed hand the coin rides than where the drawing leaves it. */
const DEEP = 16;
/** Where a hand ends, below the drawing: it draws back from the coin it left, rather than holding it. */
const LEFT_BEHIND = 9;
/** All three rise together, each a breath after the one to its left so they don't move as one. */
const STAGGER = 0.06;
/** Seconds the tile sits empty after scrolling into view, so the hands arrive once the page has settled. */
const FIRST = 0.8;

/** Beats of one hand, in seconds from its start. */
export const BEATS = { arrive: 0.55, release: 0.62, back: 0.95, settled: 1.08 };

/** Left to right: the wrist each hand sways about as it rises. */
const WRISTS = ['141 327', '228 327', '306 327'];

const DURATION = FIRST + STAGGER * 2 + BEATS.settled + 0.1;

function startOf(index: number) {
	return FIRST + STAGGER * index;
}

/**
 * The rise, shifted by `offset`: hand and coin run it with the same times and easing, a constant `DEEP`
 * apart, so SMIL keeps the coin locked in the hand until it lets go.
 */
function rise(start: number, offset: number): [Key, Key] {
	return [
		[start, `0 ${FROM_BELOW + offset}`],
		[start + BEATS.arrive, `0 ${offset}`, 'out'],
	];
}

function sway(start: number, wrist: string): Keys {
	return [
		[start, `-4 ${wrist}`],
		[start + BEATS.arrive, `0 ${wrist}`, 'out'],
	];
}

function stroke(path: ArtPath, palette: Palette, colour: string = palette.ink): SVGProps<SVGPathElement> {
	return path.fill
		? { fill: palette.coin }
		: {
				fill: 'none',
				stroke: colour,
				strokeWidth: 1.138,
				strokeLinecap: path.cap ?? 'round',
				strokeLinejoin: path.join ?? 'round',
			};
}

/** One outline of a hand: closed round the coin while it carries it, opening into the drawing as it lets go. */
function HandPath(props: { path: ArtPath; index: number; hand: number; layer: 'fill' | 'line' }) {
	const palette = use(Colours);
	const { path, index } = props;
	const closed = drawClosed(path, index, FINGERS[props.hand] ?? []);
	const start = startOf(props.hand);
	return (
		<path
			d={path.d}
			transform={path.at ? `translate(${path.at})` : undefined}
			{...(props.layer === 'fill' ? { fill: palette.surface } : stroke(path, palette))}
		>
			{closed !== path.d && (
				<Animate
					attr="d"
					keys={[
						[start + BEATS.release, closed],
						[start + BEATS.back, path.d, 'out'],
					]}
				/>
			)}
		</path>
	);
}

/**
 * Rises closed with its coin held deep in the grip, stops with the coin on its spot and the hand still a
 * little high, then opens and settles down into the drawing while the coin stays where it was left.
 */
function RisingHand(props: { hand: number }) {
	const palette = use(Colours);
	const art = HANDS[props.hand];
	if (!art) return null;
	const start = startOf(props.hand);
	const wrist = WRISTS[props.hand] ?? '0 0';
	const [lead, arrived] = rise(start, -DEEP);
	const stack = STACK[props.hand] ?? [];
	return (
		<>
			<g>
				<AnimateTransform type="translate" keys={rise(start, 0)} />
				<AnimateTransform type="rotate" additive keys={sway(start, wrist)} />
				{art.coin.map((path) => (
					<path
						key={`${path.at}${path.d}`}
						d={path.d}
						transform={path.at ? `translate(${path.at})` : undefined}
						{...stroke(path, palette, palette.euro)}
					/>
				))}
			</g>
			<g>
				<AnimateTransform
					type="translate"
					keys={[
						lead,
						arrived,
						[start + BEATS.release, `0 ${-DEEP}`],
						[start + BEATS.back, `0 ${LEFT_BEHIND + 2}`],
						[start + BEATS.settled, `0 ${LEFT_BEHIND}`],
					]}
				/>
				<AnimateTransform type="rotate" additive keys={sway(start, wrist)} />
				{(['fill', 'line'] as const).map((layer) =>
					art.hand.map(
						(path, index) =>
							!stack.includes(index) && (
								<HandPath
									key={`${layer}${path.at}${path.d}`}
									path={path}
									index={index}
									hand={props.hand}
									layer={layer}
								/>
							),
					),
				)}
				{stack.map((index) => {
					const path = art.hand[index];
					return (
						path &&
						(['fill', 'line'] as const).map((layer) => (
							<HandPath key={`${layer}${path.at}${path.d}`} path={path} index={index} hand={props.hand} layer={layer} />
						))
					);
				})}
			</g>
		</>
	);
}

/** Plays once in view: three raised hands, each drawn back a little below the coin it has just left. */
/** `sage` is the Solutions card: dark coins on the pale olive surface. */
export function CoinsTileIllustration(props: {
	label: string;
	palette?: keyof typeof PALETTES;
	/** A tall card: frames the hands, x 96 to 350, instead of the artboard's middle. */
	portrait?: boolean;
	className?: string;
}) {
	const ref = useRef<SVGSVGElement>(null);
	usePlayOnceInView(ref);
	return (
		<Colours value={PALETTES[props.palette ?? 'sand']}>
			<SmilTimeline seconds={DURATION} once>
				{/* `slice` anchored to the top: the tile is wider than the artboard, so the wrists are what gets cut. */}
				<svg
					ref={ref}
					viewBox={props.portrait ? '96 0 420 327' : '0 0 420 327'}
					preserveAspectRatio={props.portrait ? 'xMinYMin slice' : 'xMidYMin slice'}
					role="img"
					aria-label={props.label}
					className={props.className}
				>
					{HANDS.map((art, hand) => (
						<RisingHand key={art.coin[0]?.d ?? hand} hand={hand} />
					))}
				</svg>
			</SmilTimeline>
		</Colours>
	);
}
